import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  LayoutDashboard, Building2, Users, Calendar, CreditCard, 
  Plug, MessageSquare, Gift, Star, BarChart3, Settings,
  Menu, X, LogOut, ChevronRight, DollarSign, BookOpen
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
  { id: 'facilities', label: 'Tesisler', icon: Building2, path: '/admin/facilities' },
  { id: 'users', label: 'Müşteriler', icon: Users, path: '/admin/users' },
  { id: 'bookings', label: 'Rezervasyonlar', icon: Calendar, path: '/admin/bookings' },
  { id: 'payments', label: 'Ödemeler', icon: CreditCard, path: '/admin/payments' },
  { id: 'accounting', label: 'Muhasebe & Kasa', icon: DollarSign, path: '/admin/accounting' },
  { id: 'integrations', label: 'Entegrasyonlar', icon: Plug, path: '/admin/integrations' },
  { id: 'chat', label: 'Canlı Destek', icon: MessageSquare, path: '/admin/chat' },
  { id: 'campaigns', label: 'Kampanyalar', icon: Gift, path: '/admin/campaigns' },
  { id: 'reviews', label: 'Yorumlar', icon: Star, path: '/admin/reviews' },
  { id: 'reports', label: 'Raporlar', icon: BarChart3, path: '/admin/reports' },
  { id: 'blog', label: 'Blog', icon: BookOpen, path: '/admin/blog' },
  { id: 'sms-templates', label: 'SMS Şablonları', icon: MessageSquare, path: '/admin/sms-templates' },
  { id: 'settings', label: 'Ayarlar', icon: Settings, path: '/admin/settings' },
];

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside 
        className={`
          ${sidebarOpen ? 'w-64' : 'w-20'} 
          bg-white border-r border-slate-200 transition-all duration-300
          flex flex-col
        `}
      >
        {/* Logo & Toggle */}
        <div className="h-16 border-b border-slate-200 flex items-center justify-between px-4">
          {sidebarOpen ? (
            <>
              <h1 className="text-xl font-bold text-emerald-600">Admin Panel</h1>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </>
          ) : (
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setSidebarOpen(true)}
              className="mx-auto"
            >
              <Menu className="w-5 h-5" />
            </Button>
          )}
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-all duration-200
                    ${active 
                      ? 'bg-emerald-50 text-emerald-700 font-medium' 
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }
                  `}
                >
                  <Icon className={`w-5 h-5 ${active ? 'text-emerald-600' : ''}`} />
                  {sidebarOpen && (
                    <>
                      <span className="flex-1 text-left text-sm">{item.label}</span>
                      {active && <ChevronRight className="w-4 h-4" />}
                    </>
                  )}
                </button>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User Info */}
        <div className="border-t border-slate-200 p-4">
          {sidebarOpen ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold">
                  {user?.name?.charAt(0) || 'A'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {user?.name || 'Admin'}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Çıkış Yap
              </Button>
            </div>
          ) : (
            <Button 
              variant="outline" 
              size="icon"
              className="w-full"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {menuItems.find(item => isActive(item.path))?.label || 'Admin Panel'}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(BACKEND_URL, '_blank')}
            >
              Siteyi Görüntüle
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-auto p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
